import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { endpoints } from '@/api/endpoints';
import { useT } from '@/i18n/LanguageContext';
import { roleLabel } from '@/i18n/roleLabel';
import { describeStaffError } from '@/pages/Users/staff';

/**
 * ADR-084 — "My account": who I am here (in words, no ids), my password, and
 * signing out on every device. Someone signed in with a service key (the
 * existing administrator during the transition) is asked to set a password.
 */
export function SettingsPage(): JSX.Element {
  const { state, logout } = useAuth();
  const t = useT();
  const self = state.status === 'authenticated' ? state.self : null;
  const [outNote, setOutNote] = useState<string | null>(null);

  return (
    <div>
      <div className="page-header">
        <h1>{t('settings.title')}</h1>
      </div>
      <div className="card">
        <h2 className="card-title">{t('settings.yourSession')}</h2>
        {self ? (
          <dl className="kv">
            {self.display_name && (<><dt>{t('staff.name')}</dt><dd>{self.display_name}</dd></>)}
            {self.email && (<><dt>{t('login.email')}</dt><dd>{self.email}</dd></>)}
            {self.organization_name && (<><dt>{t('accept.company')}</dt><dd>{self.organization_name}</dd></>)}
            <dt>{t('settings.role')}</dt>
            <dd>{roleLabel(t, self.role)}</dd>
            <dt>{t('settings.warehouse')}</dt>
            <dd>{self.warehouse_id ? self.warehouse_name ?? t('staff.oneWarehouse') : t('settings.orgWide')}</dd>
          </dl>
        ) : (
          <p>{t('settings.notSignedIn')}</p>
        )}
        <div className="form-actions">
          <button type="button" className="danger" onClick={logout}>
            {t('nav.logOut')}
          </button>
        </div>
      </div>
      {self && self.permissions.includes('ORG.WRITE') && <ChannelContractCard />}
      {self && <PasswordCard hasPassword={self.has_password === true} viaKey={self.auth_kind === 'API_TOKEN'} />}
      {self && (
        <div className="card">
          <h2 className="card-title">{t('settings.devicesTitle')}</h2>
          <p className="field-hint">{t('settings.devicesHelp')}</p>
          {outNote && <p className="ok-note" role="status">{outNote}</p>}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => {
                if (!window.confirm(t('settings.confirmLogoutAll'))) return;
                void endpoints.logoutAll().then(
                  () => logout(),
                  () => setOutNote(t('staff.err.network')),
                );
              }}
            >
              {t('settings.logoutAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PasswordCard({ hasPassword, viaKey }: { hasPassword: boolean; viaKey: boolean }): JSX.Element {
  const t = useT();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (next !== repeat) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const r = await endpoints.setOwnPassword({ ...(hasPassword ? { currentPassword: current } : {}), newPassword: next });
      setCurrent('');
      setNext('');
      setRepeat('');
      setDone(hasPassword ? t('settings.passwordChanged', { n: r.signedOutElsewhere }) : t('settings.passwordSet'));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card wh-form" onSubmit={(e) => void onSubmit(e)} aria-labelledby="pw-title">
      <h2 id="pw-title" className="card-title">{hasPassword ? t('settings.changePassword') : t('settings.setPassword')}</h2>
      {viaKey && !hasPassword && <p className="notice">{t('settings.viaKeyNote')}</p>}
      {hasPassword && (
        <div className="field-row">
          <label htmlFor="pw-current">{t('settings.currentPassword')}</label>
          <input id="pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </div>
      )}
      <div className="field-row">
        <label htmlFor="pw-new">{t('accept.newPassword')}</label>
        <input id="pw-new" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required aria-describedby="pw-hint" />
        <p id="pw-hint" className="field-hint">{t('accept.passwordHint')}</p>
      </div>
      <div className="field-row">
        <label htmlFor="pw-repeat">{t('accept.repeat')}</label>
        <input id="pw-repeat" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required />
        {repeat && repeat !== next && <p className="field-hint warn">{t('accept.mismatch')}</p>}
      </div>
      {done && <p className="ok-note" role="status">{done}</p>}
      {error !== null && <div className="error-banner" role="alert">{describeStaffError(t, error) ?? t('staff.err.checkFields')}</div>}
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || !next || next !== repeat || (hasPassword && !current)}>
          {hasPassword ? t('settings.changePassword') : t('settings.setPassword')}
        </button>
      </div>
    </form>
  );
}

/**
 * Organisation policy (ADMIN): may a client connect a marketplace only under an ACTIVE contract?
 * On by default (the fulfilment scenario); off for operators who do not sign contracts with clients.
 */
function ChannelContractCard(): JSX.Element {
  const t = useT();
  const [required, setRequired] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    endpoints.channelContractPolicy().then(
      (r) => { if (!cancelled) setRequired(r.required); },
      () => { if (!cancelled) setFailed(true); },
    );
    return () => { cancelled = true; };
  }, []);

  async function change(next: boolean): Promise<void> {
    setBusy(true);
    setNote(null);
    setFailed(false);
    try {
      const r = await endpoints.setChannelContractPolicy(next);
      setRequired(r.required);
      setNote(t('settings.channelContractSaved'));
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card channel-policy">
      <h2 className="card-title">{t('settings.channelContractTitle')}</h2>
      <label className="checkbox-row">
        <input type="checkbox" checked={required === true} disabled={required === null || busy} onChange={(e) => void change(e.target.checked)} />
        <span>{t('settings.channelContractLabel')}</span>
      </label>
      <p className="field-hint">{t('settings.channelContractHint')}</p>
      {note && <p className="ok-note" role="status">{note}</p>}
      {failed && <p className="error-banner" role="alert">{t('err.generic')}</p>}
    </div>
  );
}
